import { Shield, Zap, TrendingUp, Sparkles, type LucideIcon } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";

interface Feature {
  icon: LucideIcon;
  title: string;
  desc: string;
  badge: string;
}

const FEATURES: Feature[] = [
  {
    icon: Shield,
    title: "Homologación DGT",
    desc: "Filtra por patinetes homologados y circula 100% legal por toda España",
    badge: "Verificado",
  },
  {
     icon: Zap,
     title: "Publica en minutos",
     desc: "Sube tu anuncio con fotos, precio y empieza a vender hoy mismo",
     badge: "Desde 9,99 €",
   },
  {
    icon: TrendingUp,
    title: "Los mejores precios",
    desc: "Compara precios de patinetes nuevos y de segunda mano al instante",
    badge: "Sin comisiones",
  },
];

const FeatureCard = ({ feature, index }: { feature: Feature; index: number }) => {
  const { ref, visible } = useReveal<HTMLDivElement>();
  const Icon = feature.icon;

  return (
    <div
      ref={ref}
      style={{ transitionDelay: visible ? `${index * 120}ms` : "0ms" }}
      className={`group relative transition-all duration-700 ease-out ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
    >
      <div className="relative h-full overflow-hidden rounded-2xl border border-border/60 bg-card/70 p-6 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-2xl hover:shadow-primary/10">
        {/* Gradiente decorativo en hover */}
        <div
          className="pointer-events-none absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          aria-hidden
        />

        <div className="flex items-start gap-4">
          {/* Icono con halo gradiente */}
          <div className="relative inline-flex shrink-0">
            <div
              className="absolute inset-0 -z-10 rounded-2xl bg-gradient-to-br from-primary to-accent opacity-30 blur-md transition-opacity duration-300 group-hover:opacity-60"
              aria-hidden
            />
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-accent text-primary-foreground shadow-lg shadow-primary/30 transition-transform duration-300 group-hover:scale-110">
              <Icon className="h-5 w-5" />
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <h3 className="font-display text-base font-bold leading-tight">{feature.title}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{feature.desc}</p>
            <div className="mt-3 inline-flex items-center gap-1 rounded-full bg-secondary/15 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-secondary-foreground ring-1 ring-secondary/20">
              {feature.badge}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FeaturesSection = () => {
  const { ref: headerRef, visible: headerVisible } = useReveal<HTMLDivElement>();

  return (
    <section className="relative overflow-hidden border-b border-border py-16">
      {/* Glow decorativo */}
      <div className="pointer-events-none absolute -top-20 right-1/3 h-64 w-64 rounded-full bg-secondary/15 blur-3xl" aria-hidden />

      <div className="container relative">
        <div
          ref={headerRef}
          className={`mx-auto max-w-2xl text-center transition-all duration-700 ${
            headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <span className="inline-flex items-center gap-1.5 rounded-full border border-secondary/30 bg-secondary/10 px-3 py-1 text-xs font-semibold text-secondary-foreground">
            <Sparkles className="h-3.5 w-3.5 text-secondary" /> Por qué elegirnos
          </span>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight md:text-4xl">
            Todo lo que necesitas para{" "}
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              comprar y vender
            </span>
          </h2>
        </div>

        <div className="mt-10 grid gap-5 md:grid-cols-3 md:gap-6">
          {FEATURES.map((feature, idx) => (
            <FeatureCard key={feature.title} feature={feature} index={idx} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
